import { useState, useEffect } from 'react';
import { galleryAPI } from '../../api/gallery';
import UploadGallery from './UploadGallery';
import EditGallery from './EditGallery';
import ImageCard from './ImageCard';
import ImageViewer from './ImageViewer';
import { showError, showSuccess, confirmDialog } from '../../utils/toast';
import './Gallery.css';

const GalleryList = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [viewingImage, setViewingImage] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Available categories
  const categories = ['all', 'Portrait', 'Landscape', 'Event', 'Product', 'Nature', 'Architecture', 'Other'];

  useEffect(() => {
    fetchImages();
  }, [selectedCategory, searchTerm]);

  const fetchImages = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {};
      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }
      if (searchTerm) {
        params.search = searchTerm;
      }
      const data = await galleryAPI.getAll(params);
      setImages(data);
    } catch (err) {
      setError('Failed to load gallery images');
      showError('Failed to load gallery images');
      console.error('Error fetching images:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSuccess = () => {
    setShowUploadModal(false);
    showSuccess('Image uploaded successfully!');
    fetchImages();
  };

  const handleEdit = (image) => {
    setEditingImage(image);
  };

  const handleEditSuccess = () => {
    setEditingImage(null);
    showSuccess('Image updated successfully!');
    fetchImages();
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmDialog('Are you sure you want to delete this image? This action cannot be undone.');
    if (!confirmed) {
      return;
    }

    try {
      await galleryAPI.delete(id);
      setDeleteConfirm(null);
      showSuccess('Image deleted successfully!');
      fetchImages();
    } catch (err) {
      showError('Failed to delete image');
      console.error('Error deleting image:', err);
    }
  };

  return (
    <div className="gallery-container">
      <div className="gallery-header">
        <div>
          <h1>Gallery Management</h1>
          <p className="gallery-subtitle">Manage your gallery images</p>
        </div>
        <button 
          className="btn-primary"
          onClick={() => setShowUploadModal(true)}
        >
          + Upload Image
        </button>
      </div>

      {/* Filters and Search */}
      <div className="gallery-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="category-filter">
          <label>Category:</label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="category-select"
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>
                {cat === 'all' ? 'All Categories' : cat}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading gallery images...</p>
        </div>
      ) : images.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📷</div>
          <h3>No images found</h3>
          <p>
            {searchTerm || selectedCategory !== 'all'
              ? 'Try adjusting your filters'
              : 'Upload your first image to get started'}
          </p>
          {!searchTerm && selectedCategory === 'all' && (
            <button 
              className="btn-primary"
              onClick={() => setShowUploadModal(true)}
            >
              Upload Image
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="gallery-stats">
            <span>{images.length} image{images.length !== 1 ? 's' : ''} found</span>
          </div>
          <div className="gallery-grid">
            {images.map((image) => (
              <ImageCard
                key={image.id}
                image={image}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onView={(img) => setViewingImage(img)}
              />
            ))}
          </div>
        </>
      )}

      {/* Image Viewer Modal */}
      {viewingImage && (
        <ImageViewer
          image={viewingImage}
          onClose={() => setViewingImage(null)}
        />
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadGallery
          onClose={() => setShowUploadModal(false)}
          onSuccess={handleUploadSuccess}
        />
      )}

      {/* Edit Modal */}
      {editingImage && (
        <EditGallery
          image={editingImage}
          onClose={() => setEditingImage(null)}
          onSuccess={handleEditSuccess}
        />
      )}
    </div>
  );
};

export default GalleryList;
